// frontend/src/components/admin/content/TopicFormModal.tsx

import { useState, FormEvent } from 'react';
import { adminService } from '../../../services/admin.service';
import { AdminLessonTopic } from '../../../types/admin.types';
import { VideoEmbedPlayer } from '../../common/VideoEmbedPlayer';

interface TopicFormModalProps {
  lessonId: string;
  topic?: AdminLessonTopic | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TopicFormModal({ lessonId, topic, onClose, onSaved }: TopicFormModalProps) {
  const [title, setTitle] = useState(topic?.title || '');
  const [description, setDescription] = useState(topic?.description || '');
  const [videoUrl, setVideoUrl] = useState(topic?.video_url || '');
  const [isPremium, setIsPremium] = useState(topic?.is_premium || false);
  const [orderIndex, setOrderIndex] = useState(topic?.order_index || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!topic;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSaving(true);
    setError(null);

    const payload = {
      title,
      description: description || null,
      video_url: videoUrl || null,
      is_premium: isPremium,
      order_index: orderIndex,
    };

    try {
      if (isEditing && topic) {
        await adminService.updateLessonTopic(topic.id, payload);
      } else {
        await adminService.createLessonTopic(lessonId, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Konu kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isEditing ? 'Konuyu Düzenle' : 'Yeni Konu Ekle'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Konu Başlığı</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="örn: Sayı Veri Tipi"
              required
            />
          </div>

          <div className="form-group">
            <label>Konu Açıklaması</label>
            <textarea
              className="input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Konunun kısa açıklaması. AI hoca slayt oluştururken bu bilgiyi temel alacaktır."
            />
          </div>

          <div className="form-group">
            <label>Destekleyici Video URL (Opsiyonel, YouTube, Vimeo, Google Drive veya MP4 bağlantısı)</label>
            <input
              className="input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoUrl && videoUrl.trim().length > 5 && (
              <div style={{ marginTop: 12, padding: 12, background: 'rgba(0,174,239,0.05)', borderRadius: 12, border: '1px dashed rgba(0,174,239,0.3)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>
                  📹 Video Önizleme:
                </div>
                <div style={{ maxWidth: 480, margin: '0 auto' }}>
                  <VideoEmbedPlayer url={videoUrl} title="Önizleme" showExternalLink={false} />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label>Sıra No (Order Index)</label>
              <input
                className="input"
                type="number"
                value={orderIndex}
                onChange={(e) => setOrderIndex(Number(e.target.value))}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}>
              <input
                type="checkbox"
                id="isPremiumTopic"
                checked={isPremium}
                onChange={(e) => setIsPremium(e.target.checked)}
                style={{ width: 20, height: 20, cursor: 'pointer' }}
              />
              <label htmlFor="isPremiumTopic" style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
                Premium Üyelik Gerektirsin
              </label>
            </div>
          </div>

          {error && <p className="auth-error" style={{ color: 'red', marginTop: 8 }}>{error}</p>}

          <div className="admin-modal-actions" style={{ marginTop: 20 }}>
            <button type="button" className="lesson-btn secondary" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="lesson-btn primary" disabled={isSaving}>
              {isSaving ? 'Kaydediliyor...' : isEditing ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
